import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  Organization,
  User,
  CreateOrganizationDto,
  CreateUserForOrganizationDto,
  CreateOrganizationWithUserDto,
} from "@autfit/shared-types";
import { organizationsAPI } from "../../services/api";
import { fetchAllPages } from "../../utils/fetchAllPages";

interface OrganizationsState {
  organizations: Organization[];
  currentOrganization: Organization | null;
  organizationUsers: User[];
  isLoading: boolean;
  isLoadingUsers: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
  };
  usersPagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
  };
}

const initialState: OrganizationsState = {
  organizations: [],
  currentOrganization: null,
  organizationUsers: [],
  isLoading: false,
  isLoadingUsers: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
  },
  usersPagination: {
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
  },
};

// Async thunks
// Loads ALL organizations (every page): the list page searches on the client,
// so a single page would silently hide schools beyond the page size.
export const fetchOrganizations = createAsyncThunk(
  "organizations/fetchOrganizations",
  async (_: void, { rejectWithValue }) => {
    try {
      const items = await fetchAllPages(organizationsAPI.getAll);
      return {
        items,
        total: items.length,
        page: 1,
        pageSize: Math.max(1, items.length),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch organizations",
      );
    }
  },
);

export const fetchOrganizationById = createAsyncThunk(
  "organizations/fetchOrganizationById",
  async (id: string, { rejectWithValue }) => {
    try {
      const organization = await organizationsAPI.getById(id);
      return organization;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch organization",
      );
    }
  },
);

export const createOrganization = createAsyncThunk(
  "organizations/createOrganization",
  async (data: CreateOrganizationDto, { rejectWithValue }) => {
    try {
      const organization = await organizationsAPI.create(data);
      return organization;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create organization",
      );
    }
  },
);

export const createOrganizationWithUser = createAsyncThunk(
  "organizations/createOrganizationWithUser",
  async (data: CreateOrganizationWithUserDto, { rejectWithValue }) => {
    try {
      const response = await organizationsAPI.createWithUser(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to create organization with user",
      );
    }
  },
);

export const updateOrganization = createAsyncThunk(
  "organizations/updateOrganization",
  async (
    { id, data }: { id: string; data: CreateOrganizationDto },
    { rejectWithValue },
  ) => {
    try {
      const organization = await organizationsAPI.update(id, data);
      return organization;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update organization",
      );
    }
  },
);

export const deleteOrganization = createAsyncThunk(
  "organizations/deleteOrganization",
  async (id: string, { rejectWithValue }) => {
    try {
      await organizationsAPI.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete organization",
      );
    }
  },
);

export const fetchOrganizationUsers = createAsyncThunk(
  "organizations/fetchOrganizationUsers",
  async (
    {
      id,
      page = 1,
      pageSize = 10,
    }: { id: string; page?: number; pageSize?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await organizationsAPI.getUsers(id, page, pageSize);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch organization users",
      );
    }
  },
);

export const createUser = createAsyncThunk(
  "organizations/createUser",
  async (
    {
      organizationId,
      data,
    }: { organizationId: string; data: CreateUserForOrganizationDto },
    { rejectWithValue },
  ) => {
    try {
      const user = await organizationsAPI.createUser(organizationId, data);
      return user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create user",
      );
    }
  },
);

const organizationsSlice = createSlice({
  name: "organizations",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentOrganization: (state) => {
      state.currentOrganization = null;
    },
    clearOrganizationUsers: (state) => {
      state.organizationUsers = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch organizations
      .addCase(fetchOrganizations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizations = action.payload.items;
        state.pagination = {
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          totalPages: Math.ceil(action.payload.total / action.payload.pageSize),
          totalItems: action.payload.total,
        };
        state.error = null;
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch organization by ID
      .addCase(fetchOrganizationById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOrganization = action.payload;
        state.error = null;
      })
      .addCase(fetchOrganizationById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create organization
      .addCase(createOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizations.unshift(action.payload);
        state.error = null;
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create organization with user
      .addCase(createOrganizationWithUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOrganizationWithUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizations.unshift(action.payload.organization);
        state.error = null;
      })
      .addCase(createOrganizationWithUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update organization
      .addCase(updateOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.organizations.findIndex(
          (org) => org.id === action.payload.id,
        );
        if (index !== -1) {
          state.organizations[index] = action.payload;
        }
        if (state.currentOrganization?.id === action.payload.id) {
          state.currentOrganization = action.payload;
        }
        state.error = null;
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete organization
      .addCase(deleteOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizations = state.organizations.filter(
          (org) => org.id !== action.payload,
        );
        if (state.currentOrganization?.id === action.payload) {
          state.currentOrganization = null;
        }
        state.error = null;
      })
      .addCase(deleteOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch organization users
      .addCase(fetchOrganizationUsers.pending, (state) => {
        state.isLoadingUsers = true;
        state.error = null;
      })
      .addCase(fetchOrganizationUsers.fulfilled, (state, action) => {
        state.isLoadingUsers = false;
        state.organizationUsers = action.payload.items;
        state.usersPagination = {
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          totalPages: Math.ceil(action.payload.total / action.payload.pageSize),
          totalItems: action.payload.total,
        };
        state.error = null;
      })
      .addCase(fetchOrganizationUsers.rejected, (state, action) => {
        state.isLoadingUsers = false;
        state.error = action.payload as string;
      })

      // Create user
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizationUsers.unshift(action.payload);
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentOrganization, clearOrganizationUsers } =
  organizationsSlice.actions;
export default organizationsSlice.reducer;
